import { Box, Skeleton, Stack, Typography } from "@mui/joy";
import { useParams } from "react-router-dom";

import { useProjectTimeline } from "../../../../hooks/query";
import { Timeline as SharedTimelineComponent } from "../../../../shared";
import type { TimelineEvent } from "../../../../shared/Timeline/Timeline";
import type { IProjectTimelineEvent } from "../../../../interfaces";

function mapEventType(event: IProjectTimelineEvent): TimelineEvent["type"] {
    if (event.Action === "status_changed") return "status-change";
    if (event.Action === "created" && event.Category === "project") return "created";
    if (event.Action === "generated") return "document";
    if (event.Action === "uploaded" && event.Category === "asset") return "asset";

    switch (event.Category) {
        case "note":
            return "note";
        case "task":
            return "task";
        case "document":
            return "document";
        case "asset":
            return "asset";
        case "milestone":
            return "milestone";
        case "inspection":
            return "inspection";
        case "estimate":
            return "estimate";
        case "integration":
            return "integration";
        case "project":
            return event.Action === "fields_changed" ? "update" : "update";
        default:
            return "update";
    }
}

function mapTimelineEvent(event: IProjectTimelineEvent): TimelineEvent {
    const type = mapEventType(event);
    const details = event.Description || event.Title || "";

    return {
        id: event.Id,
        type,
        title: event.Title,
        details,
        date: event.OccurredAt,
        user: {
            name: event.Actor?.Name || "System",
            avatarUrl: "",
        },
    };
}

export function Timeline() {
    const { id: projectId } = useParams<{ id: string }>();
    const { useListEvents } = useProjectTimeline();
    const { data, isLoading, isError } = useListEvents(projectId || "");

    const events = (Array.isArray(data) ? data : []).map(mapTimelineEvent);

    return (
        <Box m={2} ml={3} mt={-2}>
            {isLoading ? (
                <Stack spacing={3} mt={4} p={1}>
                    {[...Array(6)].map((_, i) => (
                        <Box
                            key={i}
                            display="flex"
                            gap={2}
                            alignItems="flex-start"
                        >
                            <Skeleton
                                variant="circular"
                                width={28}
                                height={28}
                            />
                            <Stack spacing={0.5} sx={{ flex: 1 }}>
                                <Skeleton
                                    variant="text"
                                    width="60%"
                                    height={14}
                                />
                                <Skeleton
                                    variant="text"
                                    width="90%"
                                    height={24}
                                    sx={{ mt: 0.5 }}
                                />
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            ) : isError ? (
                <Typography
                    level="body-sm"
                    sx={{ mt: 3, color: "danger.plainColor" }}
                >
                    Failed to load project timeline.
                </Typography>
            ) : events.length === 0 ? (
                <Typography
                    level="body-sm"
                    sx={{ mt: 2, color: "text.secondary" }}
                >
                    No timeline activity yet for this project.
                </Typography>
            ) : (
                <Box pl={2}>
                    <SharedTimelineComponent events={events} />
                </Box>
            )}
        </Box>
    );
}
